import React from 'react';
import { useNavigate } from 'react-router-dom';
import { features } from '../../config/landingData';
import { route } from '../../config/paths';
import './FeaturesSection.css';

const FeaturesSection: React.FC = () => {
  const navigate = useNavigate();

  const renderImagePattern = (imageConfig: any) => {
    if (!imageConfig || !imageConfig.images || imageConfig.images.length === 0) {
      return null;
    }

    const { pattern, images } = imageConfig;

    switch (pattern) {
      case 'single':
        return (
          <div className="image-pattern-single">
            <img
              src={images[0].src}
              alt={images[0].alt}
              className="feature-image"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
        );

      case 'two-horizontal':
        return (
          <div className="image-pattern-two-horizontal">
            {images.slice(0, 2).map((image: any, index: number) => (
              <div key={index} className="image-item">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="feature-image"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            ))}
          </div>
        );

      case 'two-vertical':
        return (
          <div className="image-pattern-two-vertical">
            {images.slice(0, 2).map((image: any, index: number) => (
              <div key={index} className="image-item">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="feature-image"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            ))}
          </div>
        );

      case 'three-stacked':
        return (
          <div className="image-pattern-three-stacked">
            {images.slice(0, 3).map((image: any, index: number) => (
              <div key={index} className="image-item">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="feature-image"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const handleCta = (cta: { type: 'route' | 'anchor'; label: string; path?: string; href?: string }) => {
    if (cta.type === 'route' && cta.path) {
      navigate(route(cta.path));
      return;
    }
    if (cta.href) {
      const el = document.querySelector(cta.href);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else window.location.hash = cta.href;
    }
  };

  return (
    <section id="features" className="features-section">
      <div className="features-container">
        <div className="features-header">
          <h2 className="features-title">Capabilities that compound</h2>
          <p className="features-subtitle">
            Voice agents for action, plus the intelligence layer that evaluates and coaches every
            conversation.
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`feature-card ${feature.imageConfig ? 'feature-card-with-image' : ''} ${
                index === 0 ? 'feature-card-lead' : ''
              }`}
            >
              <div className="feature-content-wrapper">
                <div className="feature-text-content">
                  <div className="feature-header-row">
                    <div
                      className="feature-icon-container"
                      style={{ backgroundColor: feature.color }}
                    >
                      <span className="feature-icon">{feature.icon}</span>
                    </div>
                    <h3 className="feature-name">{feature.name}</h3>
                  </div>

                  <div className="feature-content">
                    <p className="feature-description">{feature.description}</p>

                    <ul className="highlights-list">
                      {feature.highlights.map((highlight, highlightIndex) => (
                        <li key={highlightIndex} className="highlight-item">
                          <span className="highlight-bullet" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {feature.cta && (
                    <div className="feature-actions">
                      <button
                        type="button"
                        className="feature-explore"
                        onClick={() => handleCta(feature.cta)}
                      >
                        {feature.cta.label}
                      </button>
                    </div>
                  )}
                </div>

                {feature.imageConfig && (
                  <div className="feature-image-container">
                    {renderImagePattern(feature.imageConfig)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
